import React from 'react';
import { useState, useEffect } from 'react';
import api from '../../../services/api';
import {
  getMeetingDisplayName,
  formatScheduledAt,
  formatDuration
} from '../utils/meetingTemplates';

/**
 * Enkel mötessektion för projektörer
 * Visar möten för ett specifikt projekt
 */
const ProjectorMeetingsSection = ({ projectId, refreshKey, onOpenNotes, onOpenProtocol, onEditMeeting }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMeetings = async () => {
      if (!projectId) {
        setMeetings([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/meetings/project/${projectId}`);
        setMeetings(response.data.meetings || []);
      } catch (err) {
        console.error('Fel vid hämtning av möten:', err);
        setError(err.response?.data?.message || 'Kunde inte hämta möten');
      } finally {
        setLoading(false);
      }
    };

    fetchMeetings();
  }, [projectId, refreshKey]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-500">{error}</p>
    );
  }

  // Filtrera och gruppera möten
  const scheduledMeetings = meetings.filter(m =>
    m.status === 'scheduled' && new Date(m.scheduledAt) >= new Date()
  );
  const pastMeetings = meetings.filter(m =>
    m.status === 'completed' || (m.status === 'scheduled' && new Date(m.scheduledAt) < new Date())
  );

  if (meetings.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-2">Inga möten inbokade ännu</p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Kommande möten */}
      {scheduledMeetings.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-green-600 mb-2">Kommande möten</h4>
          <div className="space-y-2">
            {scheduledMeetings.map(meeting => (
              <div
                key={meeting._id}
                className="p-2.5 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {meeting.displayName || getMeetingDisplayName(meeting)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {meeting.location?.displayName} - {formatDuration(meeting.duration)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-sm font-medium text-green-700">
                        {formatScheduledAt(meeting.scheduledAt)}
                      </p>
                      <span className="text-xs text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                        Bokat
                      </span>
                    </div>
                    {onEditMeeting && (
                      <button
                        onClick={() => onEditMeeting(meeting)}
                        className="p-1.5 text-gray-400 hover:text-leadme-pink hover:bg-white rounded-full transition-colors"
                        title="Ändra möte"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                {(onOpenNotes || onOpenProtocol) && (
                  <div className="flex gap-2 mt-2 pt-2 border-t border-green-200">
                    {onOpenNotes && (
                      <button
                        onClick={() => onOpenNotes(meeting)}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                          meeting.noteCount > 0
                            ? 'text-blue-600 bg-blue-50 border border-blue-300 hover:bg-blue-100'
                            : 'text-gray-600 bg-white border border-gray-300 hover:border-leadme-pink hover:text-leadme-pink'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {meeting.noteCount > 0 ? 'Visa anteckningar' : 'Anteckning'}
                      </button>
                    )}
                    {onOpenProtocol && (
                      <button
                        onClick={() => onOpenProtocol(meeting)}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                          meeting.hasProtocol
                            ? 'text-blue-600 bg-blue-50 border border-blue-300 hover:bg-blue-100'
                            : 'text-gray-600 bg-white border border-gray-300 hover:border-leadme-pink hover:text-leadme-pink'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {meeting.hasProtocol ? 'Visa protokoll' : 'Protokoll'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tidigare möten (collapsed) */}
      {pastMeetings.length > 0 && (
        <details className="group">
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 list-none flex items-center">
            <svg className="w-3 h-3 mr-1 transform group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
            Tidigare möten ({pastMeetings.length})
          </summary>
          <div className="mt-2 space-y-1.5">
            {pastMeetings.slice(0, 3).map(meeting => (
              <div
                key={meeting._id}
                className="p-2 bg-gray-50 border border-gray-200 rounded text-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">
                    {meeting.displayName || getMeetingDisplayName(meeting)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatScheduledAt(meeting.scheduledAt)}
                  </span>
                </div>
                {(onOpenNotes || onOpenProtocol) && (
                  <div className="flex gap-2 mt-1.5 pt-1.5 border-t border-gray-200">
                    {onOpenNotes && (
                      <button
                        onClick={() => onOpenNotes(meeting)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                          meeting.noteCount > 0
                            ? 'text-blue-600 bg-blue-50 border border-blue-300 hover:bg-blue-100'
                            : 'text-gray-600 bg-white border border-gray-300 hover:border-leadme-pink hover:text-leadme-pink'
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        {meeting.noteCount > 0 ? 'Visa anteckningar' : 'Anteckning'}
                      </button>
                    )}
                    {onOpenProtocol && (
                      <button
                        onClick={() => onOpenProtocol(meeting)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                          meeting.hasProtocol
                            ? 'text-blue-600 bg-blue-50 border border-blue-300 hover:bg-blue-100'
                            : 'text-gray-600 bg-white border border-gray-300 hover:border-leadme-pink hover:text-leadme-pink'
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {meeting.hasProtocol ? 'Visa protokoll' : 'Protokoll'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default ProjectorMeetingsSection;
